import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Loader2, Save, Calendar, DollarSign, Building2, Trash2, AlertTriangle, AlertCircle, Lock, Eye, EyeOff, Link2, RefreshCw, CheckCircle2, XCircle, Plus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { format, parse, addMonths } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
  const navigate = useNavigate();
  const { activeBusiness, logout, user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [openingBalanceDate, setOpeningBalanceDate] = useState(new Date());
  const [openingCash, setOpeningCash] = useState(0);
  const [bankAccounts, setBankAccounts] = useState([{ name: '', balance: 0 }]);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  // Change password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);

  // Xero integration state
  const [xeroStatus, setXeroStatus] = useState(null);
  const [xeroConfigured, setXeroConfigured] = useState(false);
  const [xeroLoading, setXeroLoading] = useState(false);
  const [xeroSyncing, setXeroSyncing] = useState(false);
  const [xeroLastSyncSummary, setXeroLastSyncSummary] = useState(null);
  // Diagnose bank balances dialog
  const [diagBalancesLoading, setDiagBalancesLoading] = useState(false);
  const [diagBalancesData, setDiagBalancesData] = useState(null);
  const [diagBalancesOpen, setDiagBalancesOpen] = useState(false);
  const [xeroTenants, setXeroTenants] = useState([]);
  const [showTenantPicker, setShowTenantPicker] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchXeroStatus();
    // Refresh xero status when returning from OAuth callback
    const params = new URLSearchParams(window.location.search);
    if (params.get('xero') === 'connected') {
      toast.success('Xero connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('xero') === 'choose') {
      toast.info('Please choose which Xero organisation to sync from.');
      setShowTenantPicker(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [activeBusiness]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
      const obd = response.data.opening_balance_date;
      setOpeningBalanceDate(obd ? parse(obd, 'yyyy-MM-dd', new Date()) : new Date());
      setOpeningCash(response.data.opening_cash);
      const accounts = response.data.bank_accounts || [];
      setBankAccounts(accounts.length > 0 ? accounts : [{ name: '', balance: 0 }]);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Bank accounts are now managed via Xero on the Dashboard — only persist date here
      await axios.put(`${API}/settings`, {
        opening_balance_date: format(openingBalanceDate, 'yyyy-MM-dd'),
      });
      toast.success('Settings saved! Go to Dashboard to see updated forecast.');
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateBankAccount = (index, field, value) => {
    setBankAccounts((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addBankAccount = () => {
    setBankAccounts((prev) => {
      if (prev.length >= 10) {
        toast.error('Maximum 10 bank accounts allowed');
        return prev;
      }
      return [...prev, { name: '', balance: 0 }];
    });
  };

  const removeBankAccount = (index) => {
    setBankAccounts((prev) => {
      if (prev.length === 1) {
        return [{ name: '', balance: 0 }];
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const totalOpeningBalance = bankAccounts.reduce(
    (sum, a) => sum + (parseFloat(a.balance) || 0),
    0
  );

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/auth/delete-account`);
      toast.success('Account deleted successfully');
      logout();
      navigate('/login');
    } catch (error) {
      toast.error('Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  const handleChangePassword = async () => {
    const pw = newPassword || '';
    if (pw.length < 8 || !/[A-Za-z]/.test(pw) || !/\d/.test(pw) || !/[^A-Za-z0-9]/.test(pw)) {
      toast.error('New password must be at least 8 characters and include a letter, a number, and a symbol.');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to change password';
      toast.error(msg);
    } finally {
      setChangingPassword(false);
    }
  };

  const fetchXeroStatus = async () => {
    try {
      const [cfg, st] = await Promise.all([
        axios.get(`${API}/xero/config`),
        axios.get(`${API}/xero/status`),
      ]);
      setXeroConfigured(cfg.data?.configured === true);
      setXeroStatus(st.data);
      if (st.data?.connected && (st.data.available_tenant_count || 0) > 1) {
        try {
          const t = await axios.get(`${API}/xero/tenants`);
          setXeroTenants(t.data?.tenants || []);
        } catch (e) {
          setXeroTenants([]);
        }
      } else {
        setXeroTenants([]);
      }
    } catch (e) {
      // silent
    }
  };

  const handleSelectXeroTenant = async (tenantId, clearImported = false) => {
    setXeroLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/xero/select-tenant?tenant_id=${encodeURIComponent(tenantId)}&clear_imported=${clearImported}`
      );
      const cleared = (data.cleared?.invoices || 0) + (data.cleared?.bills || 0);
      toast.success(
        cleared
          ? `Switched to ${data.organization_name}. Cleared ${cleared} previously imported records.`
          : `Connected to ${data.organization_name}.`
      );
      setShowTenantPicker(false);
      await fetchXeroStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to select Xero organisation');
    } finally {
      setXeroLoading(false);
    }
  };

  const handleXeroConnect = async () => {
    setXeroLoading(true);
    try {
      const { data } = await axios.post(`${API}/xero/connect`);
      window.location.href = data.authorization_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start Xero connection');
      setXeroLoading(false);
    }
  };

  const handleXeroDisconnect = async () => {
    setXeroLoading(true);
    try {
      await axios.post(`${API}/xero/disconnect`);
      toast.success('Disconnected from Xero');
      fetchXeroStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disconnect');
    } finally {
      setXeroLoading(false);
    }
  };

  const handleXeroClearImported = async () => {
    if (!window.confirm('Delete all invoices and bills imported from Xero, then re-sync? This will not delete customers or suppliers.')) return;
    setXeroSyncing(true);
    try {
      const { data } = await axios.post(`${API}/xero/clear-imported`);
      toast.success(`Cleared ${data.invoices_deleted} invoices, ${data.bills_deleted} bills. Re-syncing…`);
      await handleXeroSync(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear');
      setXeroSyncing(false);
    }
  };

  const handleXeroSync = async (forceFull = false) => {
    setXeroSyncing(true);
    setXeroLastSyncSummary(null);
    try {
      const url = forceFull ? `${API}/xero/sync?force_full=true` : `${API}/xero/sync`;
      const { data } = await axios.post(url);
      setXeroLastSyncSummary(data);
      const created = (data.customers?.created || 0) + (data.suppliers?.created || 0)
                    + (data.invoices?.created || 0) + (data.bills?.created || 0);
      const updated = (data.invoices?.updated || 0) + (data.bills?.updated || 0);
      const removed = (data.invoices?.removed_paid || 0) + (data.bills?.removed_paid || 0);
      const parts = [];
      if (created) parts.push(`${created} new`);
      if (updated) parts.push(`${updated} updated`);
      if (removed) parts.push(`${removed} paid → removed`);
      toast.success(parts.length ? `Sync complete — ${parts.join(', ')}` : 'Sync complete — no changes');
      fetchXeroStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setXeroSyncing(false);
    }
  };

  const handleXeroAutoSyncToggle = async (enabled) => {
    try {
      await axios.put(`${API}/xero/auto-sync?enabled=${enabled}`);
      toast.success(`Auto-sync ${enabled ? 'enabled' : 'disabled'}`);
      fetchXeroStatus();
    } catch (error) {
      toast.error('Failed to update auto-sync');
    }
  };

  const handleDiagnoseBankBalances = async () => {
    setDiagBalancesLoading(true);
    setDiagBalancesData(null);
    try {
      const { data } = await axios.get(`${API}/xero/debug/bank-balances`);
      setDiagBalancesData(data);
      setDiagBalancesOpen(true);
    } catch (error) {
      const status = error.response?.status;
      if (status === 404) {
        toast.error('Diagnose endpoint not deployed yet — please redeploy production first');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to fetch diagnostic data');
      }
    } finally {
      setDiagBalancesLoading(false);
    }
  };

  const handleXeroWindowChange = async (days) => {
    try {
      await axios.put(`${API}/xero/sync-window?days=${days}`);
      const label = days === 0 ? 'all transactions' : `last ${days} days`;
      toast.success(`Sync window set to ${label}`);
      fetchXeroStatus();
    } catch (error) {
      toast.error('Failed to update sync window');
    }
  };

  const handleXeroFromDateChange = async (yyyymmdd) => {
    try {
      const params = yyyymmdd ? `?date=${yyyymmdd}` : '';
      await axios.put(`${API}/xero/sync-from-date${params}`);
      toast.success(yyyymmdd ? `Syncing from ${yyyymmdd} onwards` : 'Custom date cleared');
      fetchXeroStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update sync date');
    }
  };

  const getEndDate = () => {
    return format(addMonths(openingBalanceDate, 13), 'dd MMM yyyy');
  };

  const getFormattedDate = () => {
    return format(openingBalanceDate, 'dd MMM yyyy');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-2xl">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Settings
          </h1>
          <p className="text-slate-500 mt-1">
            Configure your cash flow planner for {activeBusiness?.name}
          </p>
        </div>

        {/* Xero Health Pill — quick-glance integration status */}
        {xeroStatus?.connected && (() => {
          const isFailed = xeroStatus.last_sync_status === 'failed';
          const isStale = (() => {
            if (!xeroStatus.last_sync) return true;
            const diff = Date.now() - new Date(xeroStatus.last_sync).getTime();
            return Number.isFinite(diff) && diff > 24 * 3600 * 1000;
          })();
          const dotColor = isFailed
            ? 'bg-rose-500'
            : isStale
              ? 'bg-amber-500'
              : 'bg-emerald-500';
          const pillBg = isFailed
            ? 'bg-rose-50 border-rose-200 dark:bg-rose-900/20 dark:border-rose-900/40'
            : isStale
              ? 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-900/40'
              : 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-900/40';
          const relTime = (() => {
            if (!xeroStatus.last_sync) return 'never';
            const diff = Date.now() - new Date(xeroStatus.last_sync).getTime();
            if (!Number.isFinite(diff)) return 'never';
            const m = Math.floor(diff / 60000);
            if (m < 1) return 'just now';
            if (m < 60) return `${m} min ago`;
            const h = Math.floor(m / 60);
            if (h < 24) return `${h}h ago`;
            const days = Math.floor(h / 24);
            return `${days}d ago`;
          })();
          const counts = [
            { n: xeroStatus.invoices_imported, label: 'invoices' },
            { n: xeroStatus.bills_imported, label: 'bills' },
            { n: xeroStatus.bank_accounts_imported, label: 'bank accounts' },
          ].filter((c) => (c.n || 0) > 0);
          return (
            <div
              className={`flex items-center gap-3 px-4 py-2.5 rounded-full border ${pillBg}`}
              data-testid="xero-health-pill"
            >
              <span className="relative flex h-2.5 w-2.5">
                {!isFailed && !isStale && (
                  <span className={`absolute inline-flex h-full w-full rounded-full ${dotColor} opacity-40 animate-ping`}></span>
                )}
                <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${dotColor}`}></span>
              </span>
              <div className="flex-1 min-w-0 text-sm">
                <span className="font-semibold text-slate-700 dark:text-slate-200">
                  {isFailed ? 'Xero sync failed' : isStale ? 'Xero out of date' : 'Xero healthy'}
                </span>
                <span className="text-slate-500 dark:text-slate-400">
                  {' '}· Last synced{' '}
                  <span className="font-medium text-slate-700 dark:text-slate-300" data-testid="xero-pill-last-sync">
                    {relTime}
                  </span>
                  {counts.length > 0 && (
                    <>
                      {' '}·{' '}
                      <span data-testid="xero-pill-counts">
                        {counts.map((c, i) => (
                          <span key={c.label}>
                            <span className="font-medium text-slate-700 dark:text-slate-300">{c.n.toLocaleString()}</span> {c.label}
                            {i < counts.length - 1 ? ' · ' : ''}
                          </span>
                        ))}
                      </span>
                    </>
                  )}
                </span>
              </div>
            </div>
          );
        })()}

        {/* Business Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Building2 className="w-5 h-5 text-primary" />
              Current Business
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
              {activeBusiness?.logo_base64 ? (
                <img 
                  src={activeBusiness.logo_base64} 
                  alt={activeBusiness.name} 
                  className="w-12 h-12 object-contain rounded"
                />
              ) : (
                <div className="w-12 h-12 bg-primary/10 rounded flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-primary" />
                </div>
              )}
              <div>
                <p className="font-semibold text-slate-900">{activeBusiness?.name}</p>
                <p className="text-sm text-slate-500">
                  Use the business selector in the header to switch or add businesses
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Currency Info */}
        <Card>
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Currency
            </CardTitle>
            <CardDescription>
              Display currency for all amounts
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-full">
                <span className="text-lg font-bold text-primary">$</span>
              </div>
              <div>
                <p className="font-medium text-slate-900">Australian Dollar (AUD)</p>
                <p className="text-sm text-slate-500">All amounts are displayed in AUD</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Xero Integration */}
        <Card data-testid="xero-integration-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Link2 className="w-5 h-5 text-primary" />
              Xero Integration
            </CardTitle>
            <CardDescription>
              Connect your Xero account to automatically import customers, suppliers, invoices and bills.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!xeroConfigured ? (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                Xero integration is not configured on the server. Contact your administrator.
              </div>
            ) : !xeroStatus?.connected ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <XCircle className="w-4 h-4 text-slate-400" />
                  Not connected
                </div>
                <Button
                  onClick={handleXeroConnect}
                  disabled={xeroLoading}
                  className="gap-2 bg-[#13B5EA] hover:bg-[#0fa0d1] text-white"
                  data-testid="xero-connect-btn"
                >
                  {xeroLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                  Connect to Xero
                </Button>
                <p className="text-xs text-slate-500">
                  You will be redirected to Xero to authorize access.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {xeroStatus.needs_tenant_selection ? (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-2 text-sm font-medium text-amber-900">
                      <AlertTriangle className="w-4 h-4" />
                      Choose which Xero organisation to sync from
                    </div>
                    <p className="text-xs text-amber-800 mt-1">
                      You authorised {xeroStatus.available_tenant_count} organisations. Pick one to enable syncing.
                    </p>
                  </div>
                ) : (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <div className="flex items-center gap-2 text-sm font-medium text-emerald-800">
                      <CheckCircle2 className="w-4 h-4" />
                      Connected to <span className="font-semibold">{xeroStatus.organization_name || 'Xero'}</span>
                      {xeroStatus.available_tenant_count > 1 && (
                        <button
                          type="button"
                          onClick={() => setShowTenantPicker((v) => !v)}
                          className="ml-auto text-xs text-emerald-700 underline hover:text-emerald-900"
                          data-testid="xero-switch-org-btn"
                        >
                          {showTenantPicker ? 'Hide options' : 'Switch organisation'}
                        </button>
                      )}
                    </div>
                    {xeroStatus.last_sync && (
                      <p className="text-xs text-emerald-700 mt-1">
                        Last sync: {new Date(xeroStatus.last_sync).toLocaleString()}
                        {xeroStatus.last_sync_status === 'failed' && (
                          <span className="ml-2 text-red-600 font-medium">(failed)</span>
                        )}
                      </p>
                    )}
                  </div>
                )}

                {/* Reconnect required — token missing scopes added after the
                    user originally authorised. Right now this triggers when
                    accounting.settings or accounting.reports is missing
                    (needed for /Accounts and /Reports/BankReconciliation). */}
                {xeroStatus.needs_reconnect && (
                  <div
                    className="p-3 bg-amber-50 border border-amber-200 rounded-lg dark:bg-amber-900/20 dark:border-amber-700"
                    data-testid="xero-reconnect-banner"
                  >
                    <div className="flex items-start gap-2 text-sm">
                      <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                      <div className="space-y-1">
                        <p className="font-semibold text-amber-900 dark:text-amber-200">
                          Reconnect Xero to enable bank balance sync
                        </p>
                        <p className="text-xs text-amber-800 dark:text-amber-300">
                          Your existing connection doesn't include the permissions needed to read bank accounts
                          and the Bank Reconciliation report. Disconnect and reconnect Xero — when you're prompted by Xero,
                          accept the new permissions to enable Statement Balance sync.
                        </p>
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-2 border-amber-400 text-amber-900 hover:bg-amber-100 dark:text-amber-200 dark:border-amber-600 dark:hover:bg-amber-900/30"
                          onClick={handleXeroDisconnect}
                          data-testid="xero-reconnect-btn"
                        >
                          Disconnect now (then click Connect again)
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tenant picker (auto-opens for needs_tenant_selection or via Switch button) */}
                {(xeroStatus.needs_tenant_selection || showTenantPicker) && xeroTenants.length > 0 && (
                  <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 dark:bg-slate-900 dark:border-slate-700 space-y-2">
                    <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                      Available Xero organisations
                    </p>
                    <ul className="space-y-2" data-testid="xero-tenants-list">
                      {xeroTenants.map((t) => (
                        <li
                          key={t.tenant_id}
                          className="flex items-center justify-between gap-2 p-2 bg-white border border-slate-200 rounded dark:bg-slate-800 dark:border-slate-700"
                          data-testid={`xero-tenant-${t.tenant_id}`}
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                              {t.name}
                              {t.is_current && (
                                <span className="ml-2 text-xs text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded dark:bg-emerald-900/40 dark:text-emerald-300">
                                  Current
                                </span>
                              )}
                            </p>
                            {t.type && (
                              <p className="text-xs text-slate-500">{t.type}</p>
                            )}
                          </div>
                          {!t.is_current && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                if (xeroStatus.tenant_id) {
                                  // Switching from a different org → ask whether to clear imported data
                                  const clear = window.confirm(
                                    `Switch sync to "${t.name}"?\n\n` +
                                    `Click OK to also DELETE all invoices and bills imported from your previous Xero organisation, ` +
                                    `then resync.\n\nClick Cancel to keep them.`
                                  );
                                  handleSelectXeroTenant(t.tenant_id, clear);
                                } else {
                                  handleSelectXeroTenant(t.tenant_id, false);
                                }
                              }}
                              disabled={xeroLoading}
                              data-testid={`xero-select-tenant-${t.tenant_id}`}
                            >
                              {xeroLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Use this'}
                            </Button>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    onClick={() => handleXeroSync(false)}
                    disabled={xeroSyncing || xeroStatus.needs_tenant_selection}
                    className="gap-2"
                    data-testid="xero-sync-btn"
                  >
                    {xeroSyncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    Sync Now
                  </Button>

                  <Button
                    variant="outline"
                    onClick={() => handleXeroAutoSyncToggle(!xeroStatus.auto_sync_enabled)}
                    data-testid="xero-auto-sync-toggle"
                  >
                    Auto-sync daily: {xeroStatus.auto_sync_enabled ? 'ON' : 'OFF'}
                  </Button>

                  <Button
                    variant="outline"
                    onClick={handleDiagnoseBankBalances}
                    disabled={diagBalancesLoading}
                    className="gap-2 text-blue-700 hover:bg-blue-50"
                    data-testid="xero-diagnose-bank-btn"
                    title="Advanced: shows the raw Xero API responses we use to compute bank balances. Use this only if bank balances don't sync."
                  >
                    {diagBalancesLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertCircle className="w-4 h-4" />}
                    Diagnose bank balances
                  </Button>

                  <Button
                    variant="outline"
                    onClick={handleXeroDisconnect}
                    disabled={xeroLoading}
                    className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid="xero-disconnect-btn"
                  >
                    <XCircle className="w-4 h-4" />
                    Disconnect
                  </Button>
                </div>
                <p className="text-xs text-slate-500 -mt-2" data-testid="xero-actions-hint">
                  <span className="font-medium text-slate-600 dark:text-slate-300">Tip:</span> use <span className="font-medium">Diagnose bank balances</span> only if bank balances don't sync — it shows raw Xero responses for troubleshooting.
                </p>

                <div className="space-y-2">
                  <Label className="text-sm">Import transactions from</Label>
                  <select
                    value={xeroStatus.sync_from_date ? 'custom' : String(xeroStatus.sync_window_days ?? 90)}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === 'custom') {
                        // Default to today minus 90 days if nothing set yet
                        const def = xeroStatus.sync_from_date || new Date(Date.now() - 90*24*3600*1000).toISOString().slice(0,10);
                        handleXeroFromDateChange(def);
                      } else {
                        handleXeroWindowChange(parseInt(v, 10));
                      }
                    }}
                    className="block w-full max-w-xs rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:bg-slate-800 dark:border-slate-700"
                    data-testid="xero-sync-window-select"
                  >
                    <option value="30">Last 30 days</option>
                    <option value="90">Last 90 days</option>
                    <option value="180">Last 6 months</option>
                    <option value="365">Last 12 months</option>
                    <option value="730">Last 2 years</option>
                    <option value="0">All time</option>
                    <option value="custom">From a specific date…</option>
                  </select>

                  {xeroStatus.sync_from_date && (
                    <div className="flex items-center gap-2 max-w-xs">
                      <Input
                        type="date"
                        value={xeroStatus.sync_from_date}
                        max={new Date().toISOString().slice(0, 10)}
                        onChange={(e) => {
                          if (e.target.value) handleXeroFromDateChange(e.target.value);
                        }}
                        className="text-sm"
                        data-testid="xero-sync-from-date-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleXeroFromDateChange(null)}
                        data-testid="xero-sync-from-date-clear"
                      >
                        Clear
                      </Button>
                    </div>
                  )}

                  <p className="text-xs text-slate-500">
                    Applies to invoices and bills only. Customers and suppliers are always fully imported.
                  </p>
                </div>

                {xeroLastSyncSummary && (
                  <div className="text-xs bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-1 dark:bg-slate-900 dark:border-slate-700">
                    <p className="font-semibold text-slate-700 dark:text-slate-200">
                      Last manual sync result {xeroLastSyncSummary.invoices?.incremental ? '(incremental)' : '(full)'}:
                    </p>
                    <p>Customers: {xeroLastSyncSummary.customers?.created || 0} new, {xeroLastSyncSummary.customers?.skipped_duplicates || 0} duplicates skipped</p>
                    <p>Suppliers: {xeroLastSyncSummary.suppliers?.created || 0} new, {xeroLastSyncSummary.suppliers?.skipped_duplicates || 0} duplicates skipped</p>
                    <p>Invoices: {xeroLastSyncSummary.invoices?.created || 0} new, {xeroLastSyncSummary.invoices?.updated || 0} updated, {xeroLastSyncSummary.invoices?.removed_paid || 0} paid → removed</p>
                    <p>Bills: {xeroLastSyncSummary.bills?.created || 0} new, {xeroLastSyncSummary.bills?.updated || 0} updated, {xeroLastSyncSummary.bills?.removed_paid || 0} paid → removed</p>
                    <p>
                      Bank accounts: {xeroLastSyncSummary.bank_accounts?.bank_accounts_imported ?? 0} imported
                      {xeroLastSyncSummary.bank_accounts?.bank_accounts_found != null && (
                        <> (found {xeroLastSyncSummary.bank_accounts.bank_accounts_found} in Xero)</>
                      )}
                      {xeroLastSyncSummary.bank_accounts?.statement_balance_resolved != null && (
                        <> · {xeroLastSyncSummary.bank_accounts.statement_balance_resolved} via bank-feed</>
                      )}
                      {xeroLastSyncSummary.bank_accounts?.fell_back_to_ledger > 0 && (
                        <span className="text-amber-700 dark:text-amber-400"> · {xeroLastSyncSummary.bank_accounts.fell_back_to_ledger} ledger fallback</span>
                      )}
                      {xeroLastSyncSummary.bank_accounts?.error && (
                        <span className="text-red-600 dark:text-red-400"> · error: {xeroLastSyncSummary.bank_accounts.error}</span>
                      )}
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Lock className="w-5 h-5 text-primary" />
              Change Password
            </CardTitle>
            <CardDescription>
              Update your account password
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 max-w-md">
              <div className="space-y-2">
                <Label htmlFor="current-password">Current Password</Label>
                <div className="relative">
                  <Input
                    id="current-password"
                    type={showCurrentPwd ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    autoComplete="current-password"
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPwd(!showCurrentPwd)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 z-10 text-slate-400 hover:text-slate-600"
                    data-testid="toggle-current-pwd"
                  >
                    {showCurrentPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showNewPwd ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Min 8 chars with letter, number & symbol"
                    autoComplete="new-password"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPwd(!showNewPwd)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 z-10 text-slate-400 hover:text-slate-600"
                    data-testid="toggle-new-pwd"
                  >
                    {showNewPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password"
                  autoComplete="new-password"
                  data-testid="confirm-password-input"
                />
              </div>
              <Button
                onClick={handleChangePassword}
                disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                className="gap-2"
                data-testid="change-password-btn"
              >
                {changingPassword ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Lock className="w-4 h-4" />
                )}
                Change Password
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Danger Zone - Delete Account */}
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <AlertTriangle className="w-5 h-5" />
              Danger Zone
            </CardTitle>
            <CardDescription>
              Permanently delete your account and all data
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <p className="text-sm text-red-800 mb-4">
                <strong>Warning:</strong> Deleting your account will permanently remove all your data including:
              </p>
              <ul className="text-sm text-red-700 list-disc list-inside mb-4 space-y-1">
                <li>All businesses and their settings</li>
                <li>All customers and suppliers</li>
                <li>All invoices and bills</li>
                <li>All other cash entries</li>
                <li>Your user profile</li>
              </ul>
              <p className="text-sm text-red-800 mb-4">
                This action <strong>cannot be undone</strong>.
              </p>
              
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="destructive" 
                    className="gap-2"
                    data-testid="delete-account-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete My Account
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2 text-red-700">
                      <AlertTriangle className="w-5 h-5" />
                      Delete Account Permanently?
                    </AlertDialogTitle>
                    <AlertDialogDescription className="space-y-2">
                      <p>
                        You are about to permanently delete your account <strong>({user?.email})</strong> and all associated data.
                      </p>
                      <p className="font-medium text-red-600">
                        This action cannot be reversed. All your businesses, customers, suppliers, invoices, bills, and cash entries will be permanently deleted.
                      </p>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDeleteAccount}
                      className="bg-red-600 hover:bg-red-700"
                      disabled={deleting}
                      data-testid="confirm-delete-account-btn"
                    >
                      {deleting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Deleting...
                        </>
                      ) : (
                        'Yes, Delete My Account'
                      )}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Diagnose bank balances dialog */}
      <AlertDialog open={diagBalancesOpen} onOpenChange={setDiagBalancesOpen}>
        <AlertDialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" data-testid="diag-bank-balances-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Xero bank-balance diagnostic</AlertDialogTitle>
            <AlertDialogDescription>
              Raw responses from Xero we use to compute each bank's balance.
              Paste this output back to support if balances still look wrong.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {diagBalancesData ? (
            <div className="space-y-3 text-xs">
              <div className="rounded bg-slate-50 dark:bg-slate-800 p-3 font-mono">
                <p>Date range: <strong>{diagBalancesData.from_date}</strong> → <strong>{diagBalancesData.to_date}</strong></p>
                <p>Accounts found: <strong>{diagBalancesData.accounts?.length ?? 0}</strong></p>
              </div>
              {(diagBalancesData.accounts || []).map((acct, i) => (
                <details key={i} className="rounded border border-slate-200 dark:border-slate-700 p-3" open={i === 0}>
                  <summary className="cursor-pointer font-semibold text-slate-700 dark:text-slate-200">
                    {acct.name} <span className="text-slate-400 font-normal">· {acct.account_id}</span>
                  </summary>
                  <pre className="mt-2 max-h-96 overflow-auto bg-slate-900 text-slate-100 p-2 rounded text-[10px]">
{JSON.stringify(acct, null, 2)}
                  </pre>
                </details>
              ))}

              {/* Top-level reports — these are what we actually use to compute
                  balances. Show them prominently. */}
              {(diagBalancesData.banksummary_multi_account || diagBalancesData.banksummary_multi_account_error) && (
                <details className="rounded border-2 border-emerald-300 dark:border-emerald-700 p-3" open>
                  <summary className="cursor-pointer font-semibold text-emerald-700 dark:text-emerald-300">
                    📊 BankSummary (multi-account) — primary balance source
                  </summary>
                  <pre className="mt-2 max-h-96 overflow-auto bg-slate-900 text-slate-100 p-2 rounded text-[10px]">
{JSON.stringify(diagBalancesData.banksummary_multi_account || {error: diagBalancesData.banksummary_multi_account_error}, null, 2)}
                  </pre>
                </details>
              )}

              {(diagBalancesData.trial_balance || diagBalancesData.trial_balance_error) && (
                <details className="rounded border-2 border-blue-300 dark:border-blue-700 p-3" open>
                  <summary className="cursor-pointer font-semibold text-blue-700 dark:text-blue-300">
                    📊 TrialBalance — fallback when BankSummary returns 0
                  </summary>
                  <pre className="mt-2 max-h-96 overflow-auto bg-slate-900 text-slate-100 p-2 rounded text-[10px]">
{JSON.stringify(diagBalancesData.trial_balance || {error: diagBalancesData.trial_balance_error}, null, 2)}
                  </pre>
                </details>
              )}

              {diagBalancesData.granted_scopes && (
                <p className="text-[11px] text-slate-500 break-all">
                  <strong>Granted scopes:</strong> {diagBalancesData.granted_scopes}
                </p>
              )}

              {diagBalancesData.error && (
                <div className="rounded bg-red-50 border border-red-200 p-3 text-red-700">
                  Error: {diagBalancesData.error}
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-500">No data.</p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="diag-bank-balances-close">Close</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                navigator.clipboard.writeText(JSON.stringify(diagBalancesData, null, 2));
                toast.success('Diagnostic JSON copied to clipboard');
              }}
              data-testid="diag-bank-balances-copy"
            >
              Copy JSON
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
